import { inject, injectable } from "inversify";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import {
  ExternalAccessTokenModel,
  ICPLAuthAuthenticationClient,
  ICPLAuthImpersonationClient,
  ImpersonationTokenModel,
  LoginRequestModel,
  TwoFactorAuthenticationResponseType,
  TwoFactorAuthModel,
} from "./api/cplauthapi/CPLAuthAPI";
import { ICompaniesService } from "./CompaniesService";
import { ISessionService } from "./SessionService";
import { toast } from "react-toastify";
import { IClientPortalCookieService } from "./ClientPortalCookieService";
import { LoginType } from "global/enums";

export interface IAuthenticationService {
  logIn(companyID: string, emailAddress: string, password: string, oneTimePassword?: string): Promise<string>;
  validateUser(companyID: string, emailAddress: string, password: string): Promise<TwoFactorAuthModel>;
  logOut(): Promise<void>;
  verifyExternalAccessUserFromToken(token: string): Promise<ExternalAccessTokenModel>;
  requestNewVerificationCode(companyID: string, emailAddress: string, password: string): Promise<void>;
  logInAsImpersonatedUser(verificationCode: string, impersonationToken: ImpersonationTokenModel): Promise<string>;
  redeemImpersonationHandoff(reference: string): Promise<ImpersonationTokenModel>;
}

@injectable()
export class AuthenticationService implements IAuthenticationService {
  @inject(SERVICE_TYPES.CPLAuthAuthenticationClient) cplAuthAuthenticationClient!: ICPLAuthAuthenticationClient;
  @inject(SERVICE_TYPES.CPLAuthImpersonationClient) cplAuthImpersonationClient!: ICPLAuthImpersonationClient;
  @inject(SERVICE_TYPES.CompaniesService) companiesService!: ICompaniesService;
  @inject(SERVICE_TYPES.SessionService) sessionService!: ISessionService;
  @inject(SERVICE_TYPES.ClientPortalCookieService) clientPortalCookieService!: IClientPortalCookieService;

  async logIn(companyID: string, emailAddress: string, password: string, oneTimePassword?: string): Promise<string> {
    const response = await this.cplAuthAuthenticationClient.verifyClientPortalUser(
      new LoginRequestModel({
        companyID,
        emailAddress,
        password,
        oneTimePassword,
      })
    );

    if (response.authenticationResponseType === TwoFactorAuthenticationResponseType.Expired) {
      toast.warning("Verfication code expired. Please request a new one");
      return "";
    } else if (response.authenticationResponseType === TwoFactorAuthenticationResponseType.Unverified) {
      toast.error("Verification code incorrect. Please re-enter code");
      return "";
    }
    await this.sessionService.setSessionTokens(response.sessionTokens!, LoginType.Normal);
    const cplToken = await this.sessionService.getCPLSessionToken();

    await this.clientPortalCookieService.updateCookiePreferences();

    if (!cplToken) {
      throw new Error("Invalid Login");
    }

    return cplToken;
  }

  async validateUser(companyID: string, emailAddress: string, password: string): Promise<TwoFactorAuthModel> {
    return await this.cplAuthAuthenticationClient.validateClientPortalUser(
      new LoginRequestModel({ companyID, emailAddress, password })
    );
  }

  async logOut(): Promise<void> {
    await this.sessionService.clearSessionTokens();
  }

  async verifyExternalAccessUserFromToken(token: string): Promise<ExternalAccessTokenModel> {
    const response = await this.cplAuthAuthenticationClient.verifyCPLExternalAccessUser(token);
    await this.sessionService.setExternalAccessSessionTokens(response, token);
    return response;
  }

  async requestNewVerificationCode(companyID: string, emailAddress: string, password: string): Promise<void> {
    await this.cplAuthAuthenticationClient.requestNewClientPortalUserVerificationCode(
      new LoginRequestModel({
        companyID,
        emailAddress,
        password,
      })
    );
  }

  async logInAsImpersonatedUser(
    verificationCode: string,
    impersonationToken: ImpersonationTokenModel
  ): Promise<string> {
    const response = await this.cplAuthAuthenticationClient.verifyImpersonationToken(
      verificationCode,
      impersonationToken
    );
    if (response.authenticationResponseType === TwoFactorAuthenticationResponseType.Expired) {
      toast.warning("Verfication code expired. Please request a new one");
      return "";
    } else if (response.authenticationResponseType === TwoFactorAuthenticationResponseType.Unverified) {
      toast.error("Verification code incorrect. Please re-enter code");
      return "";
    }
    await this.sessionService.setSessionTokens(response.sessionTokens!, LoginType.Impersonation);
    const cplToken = await this.sessionService.getCPLSessionToken();
    return cplToken;
  }

  async redeemImpersonationHandoff(reference: string): Promise<ImpersonationTokenModel> {
    return await this.cplAuthImpersonationClient.redeemImpersonationHandoff(reference);
  }
}
